import { Graphics, Text, TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';

/**
 * Shared "cute-tech" UI language (docs/06): chamfered panels with hairline
 * inlays, targeting brackets on focus.
 * Every screen builds from these so the whole game reads as one device.
 */
export interface PanelOpts {
  fill?: number;
  fillAlpha?: number;
  stroke?: number;
  strokeWidth?: number;
  /** Chamfer size — TL and BR corners are cut */
  notch?: number;
  /** Skip the inner hairline inlay */
  plain?: boolean;
}

export function chamferPanel(g: Graphics, w: number, h: number, o: PanelOpts = {}): Graphics {
  const n = Math.min(o.notch ?? 14, w / 3, h / 3);
  const fill = o.fill ?? 0x161222;
  const stroke = o.stroke ?? 0x2a2440;

  g.poly([n, 0, w, 0, w, h - n, w - n, h, 0, h, 0, n])
    .fill({ color: fill, alpha: o.fillAlpha ?? 1 })
    .stroke({ color: stroke, width: o.strokeWidth ?? 3 });

  if (!o.plain) {
    const i = 4;
    g.poly([n + i, i, w - i, i, w - i, h - n + 1, w - n + 1, h - i, i, h - i, i, n + i])
      .stroke({ color: stroke, width: 1, alpha: 0.35 });
    // Chamfer accent dashes
    g.moveTo(2, n + 3).lineTo(n + 3, 2).stroke({ color: stroke, width: 2, alpha: 0.8 });
    g.moveTo(w - n - 3, h - 2).lineTo(w - 2, h - n - 3).stroke({ color: stroke, width: 2, alpha: 0.8 });
  }
  return g;
}

/** Four corner targeting brackets — the focus indicator everywhere. */
export function targetBrackets(
  g: Graphics, w: number, h: number, color: number = COLORS.uiAccent, len = 16, off = 5,
): Graphics {
  const W = 4;
  const corners: ReadonlyArray<[number, number, 1 | -1, 1 | -1]> = [
    [-off, -off, 1, 1],
    [w + off, -off, -1, 1],
    [w + off, h + off, -1, -1],
    [-off, h + off, 1, -1],
  ];
  for (const [x, y, sx, sy] of corners) {
    g.moveTo(x + sx * len, y).lineTo(x, y).lineTo(x, y + sy * len)
      .stroke({ color, width: W, cap: 'square', join: 'miter' });
  }
  return g;
}

/** Hexagon path helper (flat-top). */
export function hexPath(g: Graphics, cx: number, cy: number, r: number): Graphics {
  const pts: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  return g.poly(pts);
}

export const UI_FONT = 'Tektur, Arial Black, sans-serif';
export const BODY_FONT = 'Arial, sans-serif';

/** Rasterize UI text at 2× so design-space screens stay crisp when scaled up */
const TEXT_RESOLUTION = 2;

export function label(
  text: string, size: number, fill: number | string = 0xffffff, spacing = 1,
): Text {
  return new Text({
    text,
    resolution: TEXT_RESOLUTION,
    style: new TextStyle({ fontFamily: UI_FONT, fontSize: size, fill, letterSpacing: spacing }),
  });
}

export function bodyText(text: string, size: number, fill: number = 0xc4c9d4, wrapWidth = 0): Text {
  return new Text({
    text,
    resolution: TEXT_RESOLUTION,
    style: new TextStyle({
      fontFamily: BODY_FONT, fontSize: size, fill,
      align: 'center',
      ...(wrapWidth > 0 ? { wordWrap: true, wordWrapWidth: wrapWidth } : {}),
      lineHeight: size * 1.35,
    }),
  });
}
